"use client"

import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import clsx from "clsx"
import * as React from "react"

import { cn } from "@/lib/utils"

type TooltipContextValue = {
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  isHovered: boolean
  setIsHovered: React.Dispatch<React.SetStateAction<boolean>>
}

const TooltipContext = React.createContext<TooltipContextValue | undefined>(
  undefined
)

const TooltipProvider = ({ children }: { children: React.ReactNode }) => (
  <TooltipPrimitive.Provider
    delayDuration={0}
    disableHoverableContent={false}
    skipDelayDuration={0}>
    {children}
  </TooltipPrimitive.Provider>
)

const Tooltip = ({
  children,
  ...props
}: React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Root>) => {
  const [open, setOpen] = React.useState(false)
  const [isHovered, setIsHovered] = React.useState(false)

  // Close tooltip on mobile scroll
  React.useEffect(() => {
    if (!open) return

    const handleTouchMove = () => {
      setOpen(false)
    }

    document.addEventListener("touchmove", handleTouchMove, { passive: true })
    return () => document.removeEventListener("touchmove", handleTouchMove)
  }, [open])

  return (
    <TooltipContext.Provider value={{ open, setOpen, isHovered, setIsHovered }}>
      <TooltipPrimitive.Root
        open={open}
        onOpenChange={(isOpen) => {
          if (!isOpen && !isHovered) {
            setOpen(false)
          } else {
            setOpen(true)
          }
        }}
        {...props}>
        {children}
      </TooltipPrimitive.Root>
    </TooltipContext.Provider>
  )
}

const TooltipTrigger = React.forwardRef<
  React.ComponentRef<typeof TooltipPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger>
>(({ className, ...props }, ref) => {
  const context = React.useContext(TooltipContext)

  if (!context) {
    throw new Error("TooltipTrigger must be used within a Tooltip component")
  }

  const { setOpen, setIsHovered } = context

  return (
    <TooltipPrimitive.Trigger
      ref={ref}
      className={cn("cursor-pointer pointer-events-auto", className)}
      onClick={(e) => {
        setOpen(true)

        // Call original onClick if provided
        if (props.onClick) {
          props.onClick(e)
        }
      }}
      onMouseEnter={() => {
        setIsHovered(true)
      }}
      onMouseLeave={() => {
        setIsHovered(false)
      }}
      onFocus={() => {
        setIsHovered(true)
      }}
      onBlur={() => {
        setIsHovered(false)
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          setOpen(false)
        }
      }}
      {...props}
    />
  )
})
TooltipTrigger.displayName = TooltipPrimitive.Trigger.displayName

const TooltipArrow = TooltipPrimitive.Arrow

type TooltipContentProps = React.ComponentPropsWithoutRef<
  typeof TooltipPrimitive.Content
> & {
  arrowClasses?: string
}

const TooltipContent = React.forwardRef<
  React.ComponentRef<typeof TooltipPrimitive.Content>,
  TooltipContentProps
>(({ className, children, sideOffset = 4, ...props }, ref) => {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
          "bg-white z-50 overflow-hidden px-3 py-1.5 text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          "max-w-[360px]",
          className
        )}
        {...props}>
        {children}
        <TooltipArrow
          width={20}
          height={10}
          className={clsx(
            "transform -translate-y-1",
            props.arrowClasses || "fill-white"
          )}
        />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  )
})
TooltipContent.displayName = TooltipPrimitive.Content.displayName

export {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
}
